'use es6';

import unescapedText from 'I18n/utils/unescapedText';
import UISelect from 'UIComponents/input/UISelect';
import memoize from 'hs-lodash/memoize';
import once from 'hs-lodash/once';
import Immutable from 'immutable';
import { BY_PRODUCT as HUBSPOT_FEATURES_BY_PRODUCT } from 'marketplace-ui-apps-core/common/constants/groupTags';
import { VIEW_ALL_APPS, useAppSearchBarQuery } from 'marketplace-ui-apps-core/search/hooks/useAppSearchBarQuery';
import { APPS_TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import InputHighlighter from 'marketplace-ui-common/search/components/InputHighlighter';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'marketplace-ui-common/search/utils';
import { getHubSpotFeatureGroupI18nKey, getHubSpotFeatureI18nKey } from 'marketplace-ui-common/utils/i18n';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import debounce from 'react-utils/debounce';
import * as FilterKey from '../../../constants/search/FilterKey';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import StorefrontFilters from '../../../records/StorefrontFilters';
import { getIsPublic } from '../../../selectors/auth';
import { scrollToTop } from '../../../utils/scroll';
import { getCategoryV2Options } from '../sidebar/filters/CategoryV2Filter';
import MobileStorefrontSearchBar from './MobileStorefrontSearchBar';
import { CopilotButtons } from './CopilotButtons';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CACHED_SEARCH_VALUES_KEY = 'ecosystem-marketplace-apps:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);

// Finds the position of a selected option in the dropdown
const findSelectedOptionPosition = (selectedOption, optionGroups) => {
  if (!(optionGroups !== null && optionGroups !== void 0 && optionGroups.length)) {
    return 0;
  }
  let dropdownPosition = 0;
  for (const group of optionGroups) {
    var _group$options;
    if (!((_group$options = group.options) !== null && _group$options !== void 0 && _group$options.length)) {
      continue;
    }

    // Filter out 'see all' links and find the selected option
    const searchableOptions = group.options.filter(opt => opt.value !== VIEW_ALL_APPS);
    const selectedOptionIndex = searchableOptions.findIndex(opt => opt.value === selectedOption.value);
    if (selectedOptionIndex !== -1) {
      return dropdownPosition + selectedOptionIndex;
    }
    dropdownPosition += searchableOptions.length;
  }
  return 0;
};
const getCategoryV2SearchOptions = memoize(() => Immutable.OrderedSet(getCategoryV2Options().map(option => Object.assign({}, option, {
  value: `${FilterKey.CATEGORY_V2}:${option.value}`,
  filterKey: FilterKey.CATEGORY_V2
}))));
const getHubSpotFeatureOptions = once(() => Object.keys(HUBSPOT_FEATURES_BY_PRODUCT).reduce((result, hubSpotFeatureGroup) => {
  /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  return result.concat(Object.values(HUBSPOT_FEATURES_BY_PRODUCT[hubSpotFeatureGroup]).map(hubSpotFeature => ({
    text: unescapedText(getHubSpotFeatureI18nKey(hubSpotFeature)),
    value: `${FilterKey.HUBSPOT_FEATURES}:${hubSpotFeature}`,
    filterKey: FilterKey.HUBSPOT_FEATURES,
    help: unescapedText('appMarketplace.storefront.searchSelect.inFeatureGroup', {
      featureGroup: unescapedText(getHubSpotFeatureGroupI18nKey(hubSpotFeatureGroup))
    })
  })));
}, Immutable.OrderedSet()));
const getMatchingOptions = (input, options) => {
  return options.filter(({
    text
  }) => {
    return UISelect.stripDiacritics(text.toLowerCase()).indexOf(input) !== -1;
  }).sortBy(({
    text
  }) => text).map(option => Object.assign({}, option, {
    dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
      text: option.text,
      value: input
    })
  })).toArray();
};
const getDefaultOptions = () => {
  const cachedSearchValues = getCachedSearchValues();
  if (!cachedSearchValues.length) {
    return [];
  }
  return [{
    text: unescapedText('appMarketplace.storefront.searchSelect.groupLabels.recentSearches'),
    options: cachedSearchValues.map(value => ({
      text: value,
      value,
      cachedSearchValue: true,
      isSpecial: true
    }))
  }];
};
const SearchSelect = ({
  appIntegrations,
  inputSize,
  isSticky,
  onAppSelect,
  onCategoryV2Select,
  onClear,
  onFilterSelect,
  onSubmit,
  readOnly = false,
  searchValue: activeSearchValue
}) => {
  const location = useLocation();
  const prevLocation = usePrevious(location);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [totalAppResults, setTotalAppResults] = useState(0);
  const [searchFilters, setSearchFilters] = useState(new StorefrontFilters({
    [FilterKey.SEARCH]: activeSearchValue
  }));
  const {
    [FilterKey.SEARCH]: searchInputValue
  } = searchFilters;
  const prevSearchInputValue = usePrevious(searchInputValue);
  const prevOpen = usePrevious(open);
  const prevActiveSearchValue = usePrevious(activeSearchValue);
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);

  // TODO track search term
  const trackSelectItem = useCallback(properties => {
    trackInteraction('select search autofill item', properties, 'storefront-select-search-autofill-item');
  }, [trackInteraction]);
  const handleResetOptions = () => {
    setOptions(null);
    setTotalAppResults(0);
  };
  const handleSetInputValue = useCallback(value => {
    setSearchFilters(searchFilters.set(FilterKey.SEARCH, value || ''));
  }, [searchFilters]);
  const debouncedHandleSetInputValue = useMemo(() => debounce(handleSetInputValue, 250), [handleSetInputValue]);
  const integrationOptions = useMemo(() => appIntegrations.map(integration => ({
    text: integration,
    value: `${FilterKey.INTEGRATIONS}:${integration}`,
    filterKey: FilterKey.INTEGRATIONS
  })), [appIntegrations]);
  const isPublicApp = useSelector(getIsPublic);
  const {
    fetcher
  } = useAppSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    grouped: true
  });
  const getMatchingApps = useCallback(input => {
    return fetcher(input).then(matches => {
      if (!matches.length) {
        return null;
      }
      return matches[0];
    });
  }, [fetcher]);
  const getMatchingCategories = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, getCategoryV2SearchOptions())).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText('appMarketplace.storefront.searchSelect.groupLabels.categories'),
        options: matches
      };
    });
  }, []);
  const getMatchingHubSpotFeatures = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, getHubSpotFeatureOptions())).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText('appMarketplace.storefront.searchSelect.groupLabels.hubSpotFeatures'),
        options: matches
      };
    });
  }, []);
  const getMatchingIntegrations = useCallback(input => {
    return Promise.resolve(getMatchingOptions(input, integrationOptions)).then(matches => {
      if (!matches.length) {
        return null;
      }
      return {
        text: unescapedText('appMarketplace.storefront.searchSelect.groupLabels.integrations'),
        options: matches
      };
    });
  }, [integrationOptions]);
  const handleClear = useCallback(() => {
    onClear();
    handleSetInputValue('');
    handleResetOptions();
  }, [handleSetInputValue, onClear]);
  const handleSubmit = useCallback(value => {
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
    onSubmit(searchFilters.set(FilterKey.SEARCH, value));
    setCachedSearchValue(value, getCachedSearchValues());
    scrollToTop();
  }, [onSubmit, searchFilters, trackInteraction]);
  const handleSearch = useCallback(input => {
    setOptions([]);
    setIsLoading(true);
    Promise.all([getMatchingApps(input), getMatchingCategories(input), getMatchingHubSpotFeatures(input), getMatchingIntegrations(input)]).then(([appsOptionGroup, categoriesOptionGroup, hubSpotFeaturesOptionGroup, integrationsOptionGroup]) => {
      setIsLoading(false);
      setOptions([appsOptionGroup, categoriesOptionGroup, hubSpotFeaturesOptionGroup, integrationsOptionGroup].filter(Boolean));
    }).catch(error => {
      console.error(error);
    });
  }, [getMatchingApps, getMatchingCategories, getMatchingHubSpotFeatures, getMatchingIntegrations]);
  const handleSelectedOptionChange = useCallback(({
    target: {
      value: selectedOption
    }
  }) => {
    handleSetInputValue('');
    handleResetOptions();
    const {
      value,
      filterKey,
      cachedSearchValue
    } = selectedOption;

    // Find the absolute position of the selected option
    const itemIndex = findSelectedOptionPosition(selectedOption, options);
    if (filterKey) {
      const [__filterKey, ...filterValues] = value.split(':');
      switch (filterKey) {
        case FilterKey.CATEGORY_V2:
          {
            onCategoryV2Select(filterValues[0]);
            trackSelectItem({
              resultType: 'category',
              value: filterValues[0],
              search: searchInputValue,
              itemIndex
            });
            break;
          }
        case FilterKey.HUBSPOT_FEATURES:
          {
            const filterValue = filterValues[0];
            onFilterSelect(filterKey, filterValue);
            trackSelectItem({
              resultType: 'hubspot feature',
              value: filterValue,
              search: searchInputValue,
              itemIndex
            });
            break;
          }
        case FilterKey.INTEGRATIONS:
          {
            const filterValue = filterValues[0];
            onFilterSelect(filterKey, filterValue);
            trackSelectItem({
              resultType: 'integration',
              value: filterValue,
              search: searchInputValue,
              itemIndex
            });
            break;
          }
        default:
          break;
      }
      return;
    }
    if (cachedSearchValue) {
      handleSubmit(value);
      trackSelectItem({
        resultType: 'recent search',
        value,
        search: searchInputValue,
        itemIndex
      });
      return;
    }
    if (value === VIEW_ALL_APPS) {
      handleSubmit(searchInputValue);
      trackSelectItem({
        resultType: 'see more',
        totalResults: totalAppResults,
        search: searchInputValue
      });
      return;
    }
    const {
      slug,
      recommended
    } = selectedOption;
    onAppSelect(slug);
    trackSelectItem({
      resultType: 'app',
      value: slug,
      isRecommended: recommended,
      search: searchInputValue,
      itemIndex
    });
  }, [handleSetInputValue, handleSubmit, onAppSelect, onCategoryV2Select, onFilterSelect, searchInputValue, totalAppResults, trackSelectItem, options]);

  /**
   * Trigger search or reset options when input value or open state changes
   */
  useEffect(() => {
    if (searchInputValue !== prevSearchInputValue || open !== prevOpen) {
      if ((open || isMobileView) && searchInputValue.length >= APPS_TYPEAHEAD_SEARCH_MIN_CHARS) {
        handleSearch(searchInputValue);
      } else {
        handleResetOptions();
      }
    }
  }, [handleSearch, searchInputValue, isMobileView, open, prevSearchInputValue, prevOpen]);

  /**
   * Update search filters when active search value changes
   */
  useEffect(() => {
    if (activeSearchValue !== prevActiveSearchValue) {
      handleSetInputValue(activeSearchValue);
    }
  }, [activeSearchValue, handleSetInputValue, prevActiveSearchValue]);

  /**
   * Close search select & reset options on navigation change
   */
  useEffect(() => {
    if (open && prevLocation && location !== prevLocation) {
      setOpen(false);
      handleResetOptions();
    }
  }, [location, open, prevLocation]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQueryView, {
      min: ScreenSizes.SM,
      children: /*#__PURE__*/_jsx(SearchBar, {
        inputSize: inputSize,
        inputValue: searchInputValue,
        isLoading: isLoading,
        onClear: handleClear,
        onOpenChange: newOpen => setOpen(newOpen),
        onInputValueChange: ({
          target: {
            value
          }
        }) => debouncedHandleSetInputValue(value),
        onSearchSubmit: handleSubmit,
        open: open,
        options: options || getDefaultOptions(),
        onSelectedOptionChange: handleSelectedOptionChange,
        placeholder: unescapedText('appMarketplace.storefront.searchSelect.searchInputPlaceholder'),
        readOnly: readOnly
      })
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: ScreenSizes.SM,
      children: /*#__PURE__*/_jsx(MobileStorefrontSearchBar, {
        onClearSearch: onClear,
        onInputValueChange: value => debouncedHandleSetInputValue(value),
        onSearchSubmit: handleSubmit,
        onSelectedOptionChange: handleSelectedOptionChange,
        placeholder: unescapedText('appMarketplace.storefront.searchSelect.searchInputPlaceholder'),
        searchTerm: searchInputValue,
        typeaheadResults: options || []
      })
    }), !isSticky && /*#__PURE__*/_jsx(CopilotButtons, {})]
  });
};
SearchSelect.propTypes = {
  appIntegrations: ImmutablePropTypes.orderedSet.isRequired,
  inputSize: PropTypes.string.isRequired,
  isSticky: PropTypes.bool,
  onAppSelect: PropTypes.func.isRequired,
  onCategoryV2Select: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onFilterSelect: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  searchValue: PropTypes.string
};
export default SearchSelect;